class ProductSiblingsLoader {
  constructor() {
    // Storefront API config
    this.accessToken = "b37bdb7cf813cdb0a5793898d4acdd9a";
    this.apiEndpoint = "/api/2024-10/graphql.json";
    this.cacheTimeout = 3600000; // 1 hour (increase from 15 mins)

    // State
    this.container = null;
    this.currentProduct = null;
    this.allProducts = null;
    this.isUpdating = false;
    
    // Track user selections independently
    this.selectedColour = null;
    this.selectedHardware = null;
  }

  /**
   * Initialize - render from cache immediately if available
   */
  async init() {
    this.container = document.querySelector(".parent-products-wrapper");
    if (!this.container) return;

    // Check if collection exists
    this.collectionHandle = this.container.dataset.collectionHandle;
    if (!this.collectionHandle) {
      this.container.setAttribute("data-filter-state", "hidden");
      return;
    }

    // Get current product data
    const dataScript = document.getElementById("product-combinations-data");
    if (!dataScript) return;

    try {
      const productData = JSON.parse(dataScript.textContent);
      this.currentProduct = productData.currentProduct;
      this.isPromoProduct = productData.isPromoProduct || false;
      this.cacheKey = `product-siblings-${this.collectionHandle}`;
      
      // Initialize selected options from current product
      this.selectedColour = this.currentProduct.baseColour || this.currentProduct.colour;
      this.selectedHardware = this.currentProduct.hardware;
    } catch (e) {
      console.error("Failed to parse product data:", e);
      return;
    }

    // Try cache first for INSTANT rendering
    const cached = this.getFromCache();
    if (cached) {
      this.renderAll(cached);
      return;
    }

    // No cache - show loading and fetch
    this.container.setAttribute("data-filter-state", "loading");
    await this.loadSiblings();

    // Handle browser back/forward
    window.addEventListener('popstate', (event) => {
      if (event.state?.productHandle) {
        this.updateProduct(event.state.productHandle);
      } else {
        window.location.reload();
      }
    });

    // Prevent any accidental navigation to product handles
    document.addEventListener('click', (e) => {
      const target = e.target.closest('a[href*="/products/"]');
      if (target && this.container.contains(target)) {
        const href = target.getAttribute('href');
        const handle = href.split('/').pop();
        
        // Check if this is a sibling product
        const isSibling = this.allProducts?.some(p => p.handle === handle);
        if (isSibling) {
          e.preventDefault();
          this.updateProduct(handle);
        }
      }
    }, true);
  }

  /**
   * Load siblings from cache or API
   */
  async loadSiblings() {
    try {
      const products = await this.fetchFromStorefrontAPI();
      this.saveToCache(products);
      this.renderAll(products);
    } catch (error) {
      console.error("Failed to load siblings:", error);
      this.container.setAttribute("data-filter-state", "hidden");
    }
  }

  /**
   * Fetch from Storefront API
   */
  async fetchFromStorefrontAPI() {
    const query = `
      query getCollectionProducts($handle: String!) {
        collection(handle: $handle) {
          products(first: 100) {
            edges {
              node {
                id
                handle
                title
                featuredImage {
                  url(transform: {maxWidth: 60})
                }
                onlineStoreUrl
                variants(first: 1) {
                  edges {
                    node {
                      availableForSale
                      quantityAvailable
                    }
                  }
                }
                metafields(identifiers: [
                  {namespace: "product_info", key: "version"},
                  {namespace: "product_info", key: "hardware"},
                  {namespace: "product_info", key: "colour"},
                  {namespace: "product_info", key: "airpower_colour"}
                ]) {
                  key
                  value
                  reference {
                    ... on Metaobject {
                      fields {
                        key
                        value
                        reference {
                          ... on MediaImage {
                            image {
                              url(transform: {maxWidth: 60})
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(this.apiEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": this.accessToken,
      },
      body: JSON.stringify({
        query,
        variables: { handle: this.collectionHandle },
      }),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();
    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    // Transform response and filter out products not published to online store
    const transformedProducts = data.data.collection.products.edges
      .filter((edge) => {
        // Filter out products that don't have an onlineStoreUrl
        // If a product is not published to the Online Store channel, this will be null
        return edge.node.onlineStoreUrl !== null;
      })
      .map((edge) => {
        const node = edge.node;
        const metafields = {};
        let colourMeta = null;

        const validMetafields = (node.metafields || []).filter(
          (mf) => mf !== null
        );

        validMetafields.forEach((mf) => {
          if (mf.key === "colour" && mf.reference?.fields) {
            colourMeta = {};
            mf.reference.fields.forEach((field) => {
              if (field.key === "label") colourMeta.label = field.value;
              else if (field.key === "colour") colourMeta.colour = field.value;
              else if (field.key === "image" && field.reference?.image) {
                colourMeta.image = field.reference.image.url;
              }
            });
          } else {
            metafields[mf.key] = mf.value;
          }
        });

      const variant = node.variants.edges[0]?.node;
      const baseColour = colourMeta?.label || metafields.airpower_colour || node.title || "";

        return {
          id: node.id,
          handle: node.handle,
          title: node.title,
          featuredImage: node.featuredImage?.url || "",
          availableForSale: variant?.availableForSale ?? true,
          quantityAvailable: variant?.quantityAvailable || 0,
          version: metafields.version || "",
          hardware: metafields.hardware || "",
          colourMetafield: colourMeta,
          baseColour: baseColour,
        };
      });
    
    return transformedProducts;
  }

  /**
   * Fetch single product data for quick switching
   */
  async fetchSingleProduct(handle) {
    const query = `
    query getProduct($handle: String!) {
      product(handle: $handle) {
        id
        handle
        title
        description
        featuredImage {
          url(transform: {maxWidth: 1000})
        }
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        compareAtPriceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        variants(first: 1) {
          edges {
            node {
              id
              availableForSale
              quantityAvailable
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
            }
          }
        }
        media(first: 10) {
          edges {
            node {
              mediaContentType
              ... on MediaImage {
                image {
                  url(transform: {maxWidth: 1000})
                  altText
                  width
                  height
                }
              }
            }
          }
        }
      }
    }
  `;

    const response = await fetch(this.apiEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": this.accessToken,
      },
      body: JSON.stringify({
        query,
        variables: { handle },
      }),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();
    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    return data.data.product;
  }

  /**
   * Update product page without reload
   */
  async updateProduct(handle) {
    try {
      // Show loading state
      document.body.style.cursor = "wait";

      // Fetch product data
      const product = await this.fetchSingleProduct(handle);

      // Update current product reference from allProducts
      const productData = this.allProducts?.find(p => p.handle === handle);
      if (productData) {
        // Check if we're switching between promo and full-price
        const previousIsPromoProduct = this.isPromoProduct;
        const promoVersions = ["Sale", "Preloved", "Bundle"];
        const newIsPromoProduct = promoVersions.includes(productData.version);
        const promoStatusChanged = previousIsPromoProduct !== newIsPromoProduct;

        this.currentProduct = {
          handle: productData.handle,
          title: productData.title,
          version: productData.version,
          hardware: productData.hardware,
          colour: productData.colourMetafield?.label || productData.baseColour,
          baseColour: productData.baseColour
        };

        // Update isPromoProduct based on the new product's version
        this.isPromoProduct = newIsPromoProduct;
        
        // Update selected options to match loaded product
        this.selectedColour = this.currentProduct.baseColour;
        this.selectedHardware = this.currentProduct.hardware;

        // Store whether we need to re-render swatches
        this.needsSwatchRerender = promoStatusChanged;
      }

      // Update URL without reload
      const newUrl = `/products/${handle}`;
      window.history.pushState({ productHandle: handle }, "", newUrl);

      // Update images
      this.updateProductImages(product);

      // Update product info
      this.updateProductInfo(product);

      // Update price
      this.updateProductPrice(product);

      // Update form
      this.updateProductForm(product);

      // Remove loading state
      document.body.style.cursor = "";

      // Update active swatch
      this.updateActiveSwatches(handle);

      // Update colour description
      this.updateColourDescription(product);

      // Update hardware inventory
      this.updateHardwareInventory(handle);

      // Update hardware buttons active state
      this.updateHardwareButtons();

      // Update colour swatch handles for new hardware
      this.updateColourSwatchHandles();

      // If switching between promo/full-price, re-render all swatches
      // This ensures only the correct promo or full-price products are shown
      if (this.needsSwatchRerender) {
        this.renderAll(this.allProducts);
        this.needsSwatchRerender = false;
      }
    } catch (error) {
      console.error("Failed to update product:", error);
      // Fallback to normal navigation
      window.location.href = `/products/${handle}`;
    }
  }

  /**
   * Update product images in gallery
   */
  updateProductImages(product) {
    const sectionId = document.querySelector('[data-section]')?.dataset.section;
    if (!sectionId) return;

    const gallery = document.querySelector(`#Slider-Gallery-${sectionId}`);
    if (!gallery) return;

    const mediaItems = product.media.edges;
    if (mediaItems.length === 0) return;

    // Build array of image URLs
    const imageUrls = mediaItems
      .filter(item => item.node.mediaContentType === 'IMAGE')
      .map(item => ({
        url: item.node.image.url,
        alt: item.node.image.altText || product.title,
        width: item.node.image.width,
        height: item.node.image.height
      }));

    if (imageUrls.length === 0) return;

    // Update main gallery images
    const galleryImages = gallery.querySelectorAll('.product__media-item img');
    let loadedCount = 0;
    const totalImages = Math.min(galleryImages.length, imageUrls.length);

    galleryImages.forEach((img, index) => {
      if (imageUrls[index]) {
        const newImg = new Image();
        newImg.onload = () => {
          img.src = imageUrls[index].url;
          img.srcset = '';
          img.alt = imageUrls[index].alt;
          if (imageUrls[index].width) img.width = imageUrls[index].width;
          if (imageUrls[index].height) img.height = imageUrls[index].height;

          loadedCount++;

          // When all images loaded, update thumbnails
          if (loadedCount === totalImages) {
            this.updateThumbnails(imageUrls, sectionId);
          }
        };
        newImg.src = imageUrls[index].url;
      }
    });

    // Reset gallery to first slide
    const firstSlide = gallery.querySelector('.product__media-item:first-child');
    if (firstSlide) {
      // Remove active class from all slides
      gallery.querySelectorAll('.product__media-item').forEach(slide => {
        slide.classList.remove('is-active');
      });
      // Add active to first slide
      firstSlide.classList.add('is-active');

      // Scroll to first
      gallery.scrollTo({ left: 0, behavior: 'auto' });
    }
  }

  /**
   * Update thumbnail images
   */
  updateThumbnails(imageUrls, sectionId) {
    const thumbnailContainer = document.querySelector(`#Slider-Thumbnails-${sectionId}`);
    if (!thumbnailContainer) return;

    const thumbnails = thumbnailContainer.querySelectorAll('.thumbnail-list__item');

    thumbnails.forEach((thumbItem, index) => {
      const thumbButton = thumbItem.querySelector('.thumbnail');
      const thumbImg = thumbItem.querySelector('.thumbnail img');

      if (imageUrls[index] && thumbImg) {
        // Update thumbnail image
        thumbImg.src = imageUrls[index].url;
        thumbImg.srcset = '';
        thumbImg.alt = imageUrls[index].alt;

        // Update aria-current on first thumbnail
        if (index === 0) {
          thumbnailContainer.querySelectorAll('button').forEach(btn => {
            btn.removeAttribute('aria-current');
          });
          if (thumbButton) {
            thumbButton.setAttribute('aria-current', 'true');
          }
        }

        // Show the thumbnail
        thumbItem.style.display = '';
      } else if (!imageUrls[index]) {
        // Hide thumbnails that don't have corresponding images
        thumbItem.style.display = 'none';
      }
    });

    // Scroll thumbnails to start
    const thumbnailSlider = thumbnailContainer.querySelector('.thumbnail-list');
    if (thumbnailSlider) {
      thumbnailSlider.scrollTo({ left: 0, behavior: 'auto' });
    }
  }

  /**
   * Update product info (title, description)
   */
  updateProductInfo(product) {
    // Update title
    const titleElement = document.querySelector(
      ".product__title h1, .product__title h2"
    );
    if (titleElement) {
      titleElement.textContent = product.title.split(" | ")[0];
    }

    // Update description
    const descElement = document.querySelector(".product__description");
    if (descElement && product.description) {
      descElement.innerHTML = product.description;
    }
  }

  /**
 * Update product price
 */
updateProductPrice(product) {
  const variant = product.variants.edges[0]?.node;
  if (!variant) return;

  const price = parseFloat(variant.price.amount);
  const comparePrice = variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : null;
  const availableForSale = variant.availableForSale;

  // Update main price
  const priceElement = document.querySelector('.price__regular .price-item--regular');
  if (priceElement) {
    priceElement.textContent = `£${price.toFixed(2)}`;
  }

  // Get price container
  const priceContainer = document.querySelector('.price');

  // Handle sale price
  const salePriceContainer = document.querySelector('.price__sale');
  const compareElement = document.querySelector('.price__sale .price-item--regular');

  if (comparePrice && comparePrice > price && salePriceContainer && compareElement) {
    compareElement.textContent = `£${comparePrice.toFixed(2)}`;
    salePriceContainer.classList.remove('visibility-hidden');
  } else if (salePriceContainer) {
    salePriceContainer.classList.add('visibility-hidden');
  }

  // Update sale badge
  const saleBadge = document.querySelector('.price .badge.price__badge-sale');
  if (saleBadge) {
    if (comparePrice && comparePrice > price && availableForSale) {
      saleBadge.classList.remove('visibility-hidden');
    } else {
      saleBadge.classList.add('visibility-hidden');
    }
  }

  // Update sold out badge
  const soldOutBadge = document.querySelector('.price .badge.price__badge-sold-out');
  if (soldOutBadge) {
    if (!availableForSale) {
      soldOutBadge.classList.remove('visibility-hidden');
    } else {
      soldOutBadge.classList.add('visibility-hidden');
    }
  }

  // Update price container class for sold out state
  if (priceContainer) {
    if (!availableForSale) {
      priceContainer.classList.add('price--sold-out');
    } else {
      priceContainer.classList.remove('price--sold-out');
    }
  }

  // Update availability and button
  const submitButton = document.querySelector('.product-form__submit');
  if (submitButton) {
    // Always remove disabled state - the variant ID is set correctly
    submitButton.removeAttribute('disabled');
    
    // Get the span element inside the button
    const buttonSpan = submitButton.querySelector('span');
    
    if (availableForSale) {
      submitButton.removeAttribute('aria-disabled');
      if (buttonSpan) {
        buttonSpan.textContent = 'Add to basket';
      }
    } else {
      submitButton.setAttribute('aria-disabled', 'true');
      if (buttonSpan) {
        buttonSpan.textContent = 'Sold out';
      }
    }
  }
}

/**
 * Update form variant ID
 */
updateProductForm(product) {
  const variant = product.variants.edges[0]?.node;
  if (!variant) return;

  // Extract numeric ID from Shopify GraphQL ID
  const numericId = variant.id.split('/').pop();

  // CRITICAL: Find the correct add-to-cart form, NOT the installment form
  // There are multiple forms with name="id" inputs on the page:
  // 1. Installment/payment terms form (product-form-installment-*)
  // 2. Main add-to-cart form (product-form-*)
  const productForm = document.querySelector('product-form form[data-type="add-to-cart-form"]');

  if (!productForm) {
    console.error('[Product Siblings] Could not find add-to-cart form');
    return;
  }

  // Update hidden variant ID input in the CORRECT form
  const variantInput = productForm.querySelector('input[name="id"]');
  if (variantInput) {
    // Set the value directly
    variantInput.value = numericId;

    // Ensure the input is not disabled
    variantInput.disabled = false;

    // Force the form to recognize the new value by dispatching a change event
    // This ensures product-form.js reads the updated value on submit
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));

    // Verification log (can be removed after testing)
    console.log('[Product Siblings] Updated variant ID to:', numericId, 'for product:', product.handle);
  } else {
    console.error('[Product Siblings] Could not find variant input in add-to-cart form');
  }

  // Reset quantity input to 1 (minimum default) to avoid cart conflicts
  const quantityInput = productForm.querySelector('input[name="quantity"]');
  if (quantityInput) {
    const minQuantity = quantityInput.getAttribute('min') || '1';
    quantityInput.value = minQuantity;
    // Don't dispatch change event - just update the value silently
  }

  // Also update the installment form's variant ID if it exists
  const installmentForm = document.querySelector('form.installment');
  if (installmentForm) {
    const installmentInput = installmentForm.querySelector('input[name="id"]');
    if (installmentInput) {
      installmentInput.value = numericId;
      installmentInput.disabled = false;
    }
  }

  // Update data attributes on product info
  const productInfo = document.querySelector('product-info');
  if (productInfo) {
    productInfo.dataset.productId = product.id.split('/').pop();
  }

  // Note: We don't publish PUB_SUB_EVENTS here because we're manually updating
  // all the page elements ourselves (images, price, form, etc.) and publishing
  // the event would cause product-info to fetch from server and create a flash
}

  /**
   * Update active swatch styling
   */
  updateActiveSwatches(handle) {
    // Remove all active states
    this.container.querySelectorAll(".colour-swatch").forEach((swatch) => {
      swatch.classList.remove("active");
    });

    // Add active state to current product
    const activeWrapper = this.container.querySelector(
      `[data-product-handle="${handle}"]`
    );
    if (activeWrapper) {
      const swatch = activeWrapper.querySelector(".colour-swatch");
      if (swatch) {
        swatch.classList.add("active");
      }
    }
    
    // Also update selectedColour to match the loaded product
    const productData = this.allProducts?.find(p => p.handle === handle);
    if (productData) {
      this.selectedColour = productData.baseColour || productData.colour;
    }
  }

  /**
   * Update colour description text
   */
  updateColourDescription(product) {
    const descriptionText = this.container.querySelector('.current-product-text');
    if (!descriptionText) return;
    
    // Try to get colour from the product data we already have
    const productData = this.allProducts?.find(p => p.handle === product.handle);
    
    if (productData?.colourMetafield?.label) {
      descriptionText.textContent = productData.colourMetafield.label;
    } else if (productData?.baseColour) {
      descriptionText.textContent = productData.baseColour;
    } else {
      // Fallback to product title
      descriptionText.textContent = product.title.split(' | ')[0];
    }
  }

  /**
 * Update hardware inventory display
 */
updateHardwareInventory(handle) {
  const productData = this.allProducts?.find(p => p.handle === handle);
  if (!productData) return;

  const inventoryContainer = this.container.querySelector('.product__inventory--hardware');
  if (!inventoryContainer) return;

  const qty = productData.quantityAvailable || 0;
  const svgWrapper = inventoryContainer.querySelector('.svg-wrapper');

  // Find the text node that comes after the svg-wrapper
  let textNode = null;
  for (let i = 0; i < inventoryContainer.childNodes.length; i++) {
    const node = inventoryContainer.childNodes[i];
    if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
      textNode = node;
      break;
    }
  }

  if (qty > 0 && qty <= 5) {
    if (svgWrapper) svgWrapper.style.color = 'rgb(238, 148, 65)';
    if (textNode) textNode.textContent = `Low stock: ${qty} left`;
  } else if (qty > 5) {
    if (svgWrapper) svgWrapper.style.color = 'rgb(62, 214, 96)';
    if (textNode) textNode.textContent = 'In stock';
  } else {
    if (svgWrapper) svgWrapper.style.color = 'rgb(200, 200, 200)';
    if (textNode) textNode.textContent = 'Out of stock';
  }
}

  /**
   * Render everything at once
   */
  renderAll(products) {
    // If products is the cache object, extract the data
    if (products.data) {
      products = products.data;
    }

    this.allProducts = products;

    // Check if products have colour metafields
    const hasColours = products.some((p) => p.colourMetafield?.label);

    // Process products - apply promo/full-price filter first
    const promoVersions = ["Sale", "Preloved", "Bundle"];
    const fullPriceVersions = ["Signature", "Limited"];

    const filteredProducts = products.filter((product) => {
      const isPromoVersion = promoVersions.includes(product.version);
      const isFullPriceVersion = fullPriceVersions.includes(product.version);

      // Filter based on current product type
      if (this.isPromoProduct && !isPromoVersion) return false;
      if (!this.isPromoProduct && !isFullPriceVersion) return false;
      return true;
    });

    const allVersions = new Set();
    const allHardware = new Set();
    const colourGroups = {};

    for (let i = 0; i < filteredProducts.length; i++) {
      const product = filteredProducts[i];

      if (product.version) allVersions.add(product.version);
      if (product.hardware) allHardware.add(product.hardware);

      if (hasColours) {
        const baseColour = product.baseColour;
        if (!colourGroups[baseColour]) {
          colourGroups[baseColour] = [];
        }
        colourGroups[baseColour].push(product);
      }
    }

    // Store filtered products for later use
    this.filteredProducts = filteredProducts;

    this.allVersions = Array.from(allVersions);
    this.allHardware = Array.from(allHardware);
    this.hasColourMetafields = hasColours; // Store for later use
    this.colourGroups = hasColours ? colourGroups : null;

    // Build all HTML at once
    let swatchesHTML = "";
    let hardwareHTML = "";
    let versionHTML = "";

    // Render swatches
    if (hasColours) {
      swatchesHTML = this.buildColourSwatchesHTML(colourGroups);
    } else {
      swatchesHTML = this.buildImageSwatchesHTML(filteredProducts);
    }

    // Render hardware
    if (this.allHardware.length > 1) {
      hardwareHTML = this.buildHardwareHTML(filteredProducts);
    }

    // Render version filters
    if (this.allVersions.length > 1) {
      versionHTML = this.buildVersionHTML();
    }

    // Insert all HTML in one DOM update
    this.container.querySelector(".colour-swatches-grid").innerHTML =
      swatchesHTML;
    this.container.querySelector(".hardware-filter-container").innerHTML =
      hardwareHTML;
    this.container.querySelector(".version-filter-container").innerHTML =
      versionHTML;

    // Initialize interactions
    this.initializeInteractions(hasColours);

    // Reveal section immediately (no transition if from cache)
    this.container.setAttribute("data-filter-state", "ready");
  }

  /**
   * Build colour swatches HTML
   */
  buildColourSwatchesHTML(colourGroups) {
    let html = "";

    Object.entries(colourGroups).forEach(([baseColour, products]) => {
      // Find product matching current hardware (or selected hardware)
      const targetHardware = this.selectedHardware || this.currentProduct.hardware;
      
      let targetProduct = products.find(
        (p) => p.hardware === targetHardware
      );

      // Check if this colour + hardware combination exists
      const productExistsForCurrentHardware = products.some(
        (p) => p.hardware === targetHardware
      );

      // If no match for current hardware, use first product in the colour group
      if (!targetProduct) {
        targetProduct = products[0];
      }

      const isActive = baseColour === (this.selectedColour || this.currentProduct.baseColour);

      const colourMeta = targetProduct.colourMetafield;

      // All swatches are always clickable, but show visual indicator if unavailable
      const wrapperClass = `colour-swatch-wrapper ${!productExistsForCurrentHardware ? "not-available" : ""}`;
      const swatchClass = `colour-swatch ${isActive ? "active" : ""} ${!productExistsForCurrentHardware ? "not-available" : ""}`;

      let swatchContent = "";
      if (colourMeta?.image) {
        swatchContent = `<img class="colour-swatch__image" src="${colourMeta.image}" alt="${baseColour}" width="28" height="28" loading="eager" decoding="async">`;
      } else if (colourMeta?.colour) {
        swatchContent = `<span class="colour-swatch__color" style="background-color: ${colourMeta.colour};"></span>`;
      } else {
        swatchContent = `<span class="colour-swatch__color" style="background-color: #cccccc;"></span>`;
      }

      html += `
  <div class="${wrapperClass}"
       data-base-colour="${baseColour}"
       data-version="${targetProduct.version}"
       data-product-handle="${targetProduct.handle}">
    <button type="button" class="${swatchClass}"
            aria-label="${baseColour}"
            data-swatch-click="true">
      ${swatchContent}
    </button>
  </div>
`;
    });

    return html;
  }

  /**
   * Build image swatches HTML (for products without colour metafields)
   */
  buildImageSwatchesHTML(products) {
    let html = "";

    // Filter products
    const promoVersions = ["Sale", "Preloved", "Bundle"];
    const fullPriceVersions = ["Signature", "Limited"];

    let productsToShow = products.filter((product) => {
      const isPromoVersion = promoVersions.includes(product.version);
      const isFullPriceVersion = fullPriceVersions.includes(product.version);

      if (this.isPromoProduct && !isPromoVersion) return false;
      if (!this.isPromoProduct && !isFullPriceVersion) return false;
      return true;
    });

    // Filter by hardware if applicable
    if (this.allHardware.length > 1 && (this.selectedHardware || this.currentProduct.hardware)) {
      const targetHardware = this.selectedHardware || this.currentProduct.hardware;
      productsToShow = productsToShow.filter(
        (p) => p.hardware === targetHardware
      );
    }

    productsToShow.forEach((product) => {
      const isActive = product.handle === this.currentProduct.handle;

      // All swatches always clickable - no disabled states
      const wrapperClass = `colour-swatch-wrapper`;
      const swatchClass = `colour-swatch ${isActive ? "active" : ""}`;

      const swatchContent = product.featuredImage
        ? `<img class="colour-swatch__image" src="${product.featuredImage}" alt="${product.title}" width="28" height="28" loading="eager" decoding="async">`
        : `<span class="colour-swatch__color" style="background-color: #cccccc;"></span>`;

      html += `
  <div class="${wrapperClass}" 
       data-product-handle="${product.handle}"
       data-product-title="${product.title}"
       data-hardware="${product.hardware || ""}"
       data-version="${product.version || ""}">
    <button type="button" class="${swatchClass}" 
            aria-label="${product.title}"
            data-swatch-click="true">
      ${swatchContent}
    </button>
  </div>
`;
    });

    return html;
  }

  /**
   * Build hardware section HTML
   */
  buildHardwareHTML(products) {
    const orderedHardware = ["Gold", "Silver"].filter((hw) =>
      this.allHardware.includes(hw)
    );

    const hasColourMeta = products.some((p) => p.colourMetafield?.label);

    const hardwareButtons = orderedHardware
      .map((hardware) => {
        const isActive = hardware === (this.selectedHardware || this.currentProduct.hardware);

        // Check if this hardware + colour combination exists
        let matchingProduct;
        if (hasColourMeta) {
          matchingProduct = products.find(
            (p) =>
              p.baseColour === (this.selectedColour || this.currentProduct.baseColour) &&
              p.hardware === hardware
          );
        } else {
          matchingProduct = products.find((p) => p.hardware === hardware);
        }

        const matchingProductExists = !!matchingProduct;

        // All hardware buttons are always clickable - no disabled states
        let buttonClass = "hardware-filter-button";
        if (isActive) {
          buttonClass += " active";
        } else if (matchingProductExists) {
          buttonClass += " inactive";
        } else {
          buttonClass += " not-available";
        }

        return `
          <button class="${buttonClass}"
                  data-hardware="${hardware}">
            ${hardware.toUpperCase()}
          </button>
        `;
      })
      .join("");

    // Get inventory status for currently displayed product
    const currentProductData = this.allProducts?.find(
      (p) => p.handle === this.currentProduct.handle
    );

    let inventoryHTML = "";
    if (currentProductData) {
      const qty = currentProductData.quantityAvailable;
      const iconSVG =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15" aria-hidden="true" focusable="false" class="icon icon-inventory-status"><circle cx="7.5" cy="7.5" r="6" fill="none" stroke="currentColor" stroke-miterlimit="10" opacity="0.3"/><circle cx="7.5" cy="7.5" r="3" fill="currentColor"/></svg>';

      if (qty > 0 && qty <= 5) {
        inventoryHTML = `
          <div class="product__inventory product__inventory--hardware" role="status">
            <span class="svg-wrapper" style="color: rgb(238, 148, 65)">
              ${iconSVG}
            </span>Low stock: ${qty} left
          </div>
        `;
      } else if (qty > 5) {
        inventoryHTML = `
          <div class="product__inventory product__inventory--hardware" role="status">
            <span class="svg-wrapper" style="color: rgb(62, 214, 96)">
              ${iconSVG}
            </span>In stock
          </div>
        `;
      } else {
        inventoryHTML = `
          <div class="product__inventory product__inventory--hardware" role="status">
            <span class="svg-wrapper" style="color: rgb(200, 200, 200)">
              ${iconSVG}
            </span>Out of stock
          </div>
        `;
      }
    }

    return `
      <div class="filter-section hardware-filter-section">
        <div class="filter-label">HARDWARE</div>
        <div class="filter-content">
          <div class="filter-options">
            ${hardwareButtons}
          </div>
          ${inventoryHTML}
        </div>
      </div>
    `;
  }

  /**
   * Build version filters HTML
   */
  buildVersionHTML() {
    const versionOrder = this.isPromoProduct
      ? ["Sale", "Preloved", "Bundle"]
      : ["Signature", "Limited"];

    const sortedVersions = versionOrder.filter((v) =>
      this.allVersions.includes(v)
    );

    const buttonsHTML = `
      <button class="version-filter-button active" data-version="all">ALL</button>
      ${sortedVersions
        .map(
          (version) => `
        <button class="version-filter-button" data-version="${version}">
          ${version.toUpperCase()}
        </button>
      `
        )
        .join("")}
    `;

    return `
      <div class="filter-section version-filter-section">
        <div class="filter-options">
          ${buttonsHTML}
        </div>
      </div>
    `;
  }

  /**
   * Initialize interactions
   */
  initializeInteractions(hasColours) {
    // Swatch clicks - handle without page reload
    this.initializeSwatchListeners();

    // Hardware button clicks
    this.initializeHardwareButtons();

    // Version filter clicks
    const versionButtons = this.container.querySelectorAll(
      ".version-filter-button"
    );
    versionButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const version = button.dataset.version;

        versionButtons.forEach((b) => b.classList.remove("active"));
        button.classList.add("active");

        this.saveVersionFilter(version);

        const swatches = this.container.querySelectorAll(
          ".colour-swatch-wrapper"
        );
        swatches.forEach((swatch) => {
          if (version === "all" || swatch.dataset.version === version) {
            swatch.classList.remove("filtered-out");
          } else {
            swatch.classList.add("filtered-out");
          }
        });
      });
    });

    // Restore saved filter
    const savedFilter = this.getVersionFilter();
    if (savedFilter !== "all") {
      const targetButton = this.container.querySelector(
        `[data-version="${savedFilter}"]`
      );
      if (targetButton) {
        targetButton.click();
      }
    }
  }
  

  /**
   * Update button state when no valid product combination exists
   */
  updateButtonState(product) {
    const submitButton = document.querySelector('.product-form__submit');
    if (!submitButton) return;

    // Get the span element inside the button
    const buttonSpan = submitButton.querySelector('span');

    if (!product) {
      // No valid combination - show "Unavailable"
      submitButton.removeAttribute('disabled');
      submitButton.setAttribute('aria-disabled', 'true');
      if (buttonSpan) {
        buttonSpan.textContent = 'Unavailable';
      }
    } else {
      // Valid product - check availability
      if (product.availableForSale) {
        submitButton.removeAttribute('disabled');
        submitButton.removeAttribute('aria-disabled');
        if (buttonSpan) {
          buttonSpan.textContent = 'Add to basket';
        }
      } else {
        submitButton.removeAttribute('disabled');
        submitButton.setAttribute('aria-disabled', 'true');
        if (buttonSpan) {
          buttonSpan.textContent = 'Sold out';
        }
      }
    }
  }

  /**
   * Update hardware button active states without re-rendering
   */
  updateHardwareButtons() {
    const hardwareButtons = this.container.querySelectorAll(
      ".hardware-filter-button"
    );

    hardwareButtons.forEach((button) => {
      const buttonHardware = button.dataset.hardware;
      const isActive = buttonHardware === (this.selectedHardware || this.currentProduct.hardware);

      // Find matching product for this hardware and current colour
      let matchingProduct;
      if (this.hasColourMetafields && (this.selectedColour || this.currentProduct.baseColour)) {
        matchingProduct = this.allProducts.find(
          (p) => p.baseColour === (this.selectedColour || this.currentProduct.baseColour) && p.hardware === buttonHardware
        );
      } else {
        matchingProduct = this.allProducts.find((p) => p.hardware === buttonHardware);
      }

      const matchingProductExists = !!matchingProduct;

      // Remove all state classes first
      button.classList.remove("active", "inactive", "not-available");

      // Apply correct state - all buttons are always enabled for clicking
      if (isActive) {
        button.classList.add("active");
      } else if (matchingProductExists) {
        button.classList.add("inactive");
      } else {
        button.classList.add("not-available");
      }
      
      // Never disable hardware buttons
      button.disabled = false;
    });
  }

  /**
   * Update colour swatch product handles to match current hardware
   */
  updateColourSwatchHandles() {
    // For products with colour metafields, update handles to match selected hardware
    if (this.hasColourMetafields) {
      const swatchWrappers = this.container.querySelectorAll(".colour-swatch-wrapper");
      const targetHardware = this.selectedHardware || this.currentProduct.hardware;

      swatchWrappers.forEach((wrapper) => {
        const baseColour = wrapper.dataset.baseColour;

        if (baseColour) {
          // Find the product with this colour and target hardware
          const promoVersions = ["Sale", "Preloved", "Bundle"];
          const fullPriceVersions = ["Signature", "Limited"];

          const targetProduct = this.allProducts.find(
            (p) => {
              // Must match colour and hardware
              if (p.baseColour !== baseColour || p.hardware !== targetHardware) {
                return false;
              }

              // Must match promo/full-price filter
              const isPromoVersion = promoVersions.includes(p.version);
              const isFullPriceVersion = fullPriceVersions.includes(p.version);

              if (this.isPromoProduct && !isPromoVersion) {
                return false;
              }
              if (!this.isPromoProduct && !isFullPriceVersion) {
                return false;
              }

              return true;
            }
          );

          const button = wrapper.querySelector("button.colour-swatch");

          if (targetProduct) {
            wrapper.dataset.productHandle = targetProduct.handle;
            wrapper.dataset.version = targetProduct.version;

            // Remove not-available class if product exists
            wrapper.classList.remove("not-available");
            if (button) {
              button.classList.remove("not-available");
            }
          } else {
            // No product for this combination - show visual indicator but keep clickable
            wrapper.classList.add("not-available");
            if (button) {
              button.classList.add("not-available");
            }
          }
        }
      });
    } else {
      // For image swatches (no colour metafields), re-render the entire swatch grid
      // because different products are shown for different hardware
      if (this.allHardware.length > 1) {
        const swatchesHTML = this.buildImageSwatchesHTML(this.allProducts);
        this.container.querySelector(".colour-swatches-grid").innerHTML = swatchesHTML;

        // Re-attach event listeners for new swatches
        this.initializeSwatchListeners();
      }
    }
  }

  /**
   * Initialize swatch click listeners
   */
  initializeSwatchListeners() {
    const swatchButtons = this.container.querySelectorAll(
      '[data-swatch-click="true"]'
    );
    const descriptionText = this.container.querySelector('.current-product-text');

    swatchButtons.forEach((button) => {
      button.addEventListener("click", async (e) => {
        e.preventDefault();
        
        // Prevent multiple clicks while updating
        if (this.isUpdating) return;

        const wrapper = button.closest(".colour-swatch-wrapper");
        if (!wrapper) return;

        // Get the selected colour
        const selectedColour = wrapper.dataset.baseColour || wrapper.dataset.productTitle;
        if (!selectedColour) return;

        // Update selected colour
        this.selectedColour = selectedColour;

        // Update active state on all swatches immediately
        this.container.querySelectorAll(".colour-swatch").forEach((swatch) => {
          swatch.classList.remove("active");
        });
        button.classList.add("active");

        // Check if combination exists
        const targetHardware = this.selectedHardware || this.currentProduct.hardware;
        const promoVersions = ["Sale", "Preloved", "Bundle"];
        const fullPriceVersions = ["Signature", "Limited"];

        const matchingProduct = this.allProducts.find((p) => {
          // Match colour
          const colourMatch = this.hasColourMetafields 
            ? p.baseColour === selectedColour
            : p.handle === wrapper.dataset.productHandle;
          
          if (!colourMatch) return false;

          // Match hardware
          if (p.hardware !== targetHardware) return false;

          // Match promo/full-price filter
          const isPromoVersion = promoVersions.includes(p.version);
          const isFullPriceVersion = fullPriceVersions.includes(p.version);

          if (this.isPromoProduct && !isPromoVersion) return false;
          if (!this.isPromoProduct && !isFullPriceVersion) return false;

          return true;
        });

        if (matchingProduct) {
          // Valid combination found - load the product
          this.isUpdating = true;
          try {
            await this.updateProduct(matchingProduct.handle);
          } finally {
            this.isUpdating = false;
          }
        } else {
          // No valid combination - update button state only
          this.updateButtonState(null);
        }
      });

      // Add hover events to update the colour text
      button.addEventListener("mouseenter", () => {
        if (!descriptionText) return;

        const wrapper = button.closest(".colour-swatch-wrapper");
        if (!wrapper) return;

        // Get the colour name from the wrapper
        let colourName = wrapper.dataset.baseColour;

        // If no baseColour, try to get from product title
        if (!colourName && wrapper.dataset.productTitle) {
          colourName = wrapper.dataset.productTitle;
        }

        if (colourName) {
          descriptionText.textContent = colourName;
        }
      });

      button.addEventListener("mouseleave", () => {
        if (!descriptionText) return;

        // Restore current product colour
        if (this.currentProduct.baseColour) {
          descriptionText.textContent = this.currentProduct.baseColour;
        } else if (this.currentProduct.colour) {
          descriptionText.textContent = this.currentProduct.colour;
        }
      });
    });
  }

  /**
   * Initialize hardware button listeners
   */
  initializeHardwareButtons() {
    const hardwareButtons = this.container.querySelectorAll(
      ".hardware-filter-button"
    );

    hardwareButtons.forEach((button) => {
      button.addEventListener("click", async (e) => {
        e.preventDefault();

        // Prevent multiple clicks while updating
        if (this.isUpdating) return;
        
        const targetHardware = button.dataset.hardware;
        if (!targetHardware) return;

        // Update selected hardware
        this.selectedHardware = targetHardware;

        // Update active state on all hardware buttons immediately
        hardwareButtons.forEach((btn) => {
          btn.classList.remove("active");
          btn.classList.add("inactive");
        });
        button.classList.remove("inactive");
        button.classList.add("active");

        // Check if combination exists
        const selectedColour = this.selectedColour || this.currentProduct.baseColour;
        const promoVersions = ["Sale", "Preloved", "Bundle"];
        const fullPriceVersions = ["Signature", "Limited"];

        const matchingProduct = this.allProducts.find((p) => {
          // Match hardware
          if (p.hardware !== targetHardware) return false;

          // Match colour
          if (this.hasColourMetafields) {
            if (p.baseColour !== selectedColour) return false;
          }

          // Match promo/full-price filter
          const isPromoVersion = promoVersions.includes(p.version);
          const isFullPriceVersion = fullPriceVersions.includes(p.version);

          if (this.isPromoProduct && !isPromoVersion) return false;
          if (!this.isPromoProduct && !isFullPriceVersion) return false;

          return true;
        });

        if (matchingProduct) {
          // Valid combination found - load the product
          this.isUpdating = true;
          try {
            await this.updateProduct(matchingProduct.handle);
            
            // Update colour swatches to reflect new hardware
            this.updateColourSwatchHandles();
          } finally {
            this.isUpdating = false;
          }
        } else {
          // No valid combination - update button state and swatches only
          this.updateButtonState(null);
          this.updateColourSwatchHandles();
        }
      });
    });
  }

  /**
   * Version filter persistence
   */
  saveVersionFilter(version) {
    try {
      sessionStorage.setItem(
        `version-filter-${this.collectionHandle}`,
        version
      );
    } catch (e) {}
  }

  getVersionFilter() {
    try {
      return (
        sessionStorage.getItem(`version-filter-${this.collectionHandle}`) ||
        "all"
      );
    } catch (e) {
      return "all";
    }
  }

  /**
   * Cache methods - with metadata
   */
  getFromCache() {
    try {
      const cached = sessionStorage.getItem(this.cacheKey);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      const { data, timestamp, collectionHandle } = parsed;

      if (
        collectionHandle === this.collectionHandle &&
        Date.now() - timestamp < this.cacheTimeout
      ) {
        return parsed; // Return the whole cache object with metadata
      }

      sessionStorage.removeItem(this.cacheKey);
      return null;
    } catch (e) {
      sessionStorage.removeItem(this.cacheKey);
      return null;
    }
  }

  saveToCache(products) {
    try {
      // Don't cache empty results - they might be errors
      if (!products || products.length === 0) {
        return;
      }
      
      const cacheData = {
        data: products,
        timestamp: Date.now(),
        collectionHandle: this.collectionHandle,
      };
      sessionStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
    } catch (e) {
      console.warn("Failed to cache products:", e);
    }
  }
}

// Initialize immediately
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    const loader = new ProductSiblingsLoader();
    loader.init();
  });
} else {
  const loader = new ProductSiblingsLoader();
  loader.init();
}
